/**
 * Admin page for system management.
 * 
 * Features:
 * - Dashboard tab with system statistics
 * - Users tab with full user management
 * - Pending tab for quick approval queue
 * 
 * Only accessible to users with 'author' role.
 * 
 * Called by:
 * - App.tsx as route for /admin
 */
import { useState } from 'react';
import { Typography, Tabs, Badge, Space, Alert } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { getAdminStats } from '../services/api';
import DashboardStats from '../components/admin/DashboardStats';
import UserManagement from '../components/admin/UserManagement';
import ModerationTab from '../components/admin/ModerationTab';

const { Title } = Typography;

// =============================================================================
// Component
// =============================================================================

/**
 * AdminPage component.
 * Admin panel with tabs for different management functions.
 */
function AdminPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  // Fetch stats for pending badge
  const { data: stats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: getAdminStats,
  });

  // Check if user is author
  if (!user || user.role !== 'author') {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '48px 24px' }}>
        <Alert
          message="Access Denied"
          description="You do not have permission to access the admin panel. Only the author can access this page."
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Tab items with badges
  const tabItems = [
    {
      key: 'dashboard',
      label: (
        <Space>
          <DashboardOutlined />
          <span>Dashboard</span>
        </Space>
      ),
      children: <DashboardStats />,
    },
    {
      key: 'users',
      label: (
        <Space>
          <TeamOutlined />
          <span>Users</span>
        </Space>
      ),
      children: <UserManagement />,
    },
    {
      key: 'pending',
      label: (
        <Space>
          <ClockCircleOutlined />
          <span>Pending</span>
          {stats?.pendingUsers ? (
            <Badge count={stats.pendingUsers} size="small" />
          ) : null}
        </Space>
      ),
      children: <UserManagement pendingOnly />,
    },
    {
      key: 'moderation',
      label: (
        <Space>
          <WarningOutlined />
          <span>Moderation</span>
          {stats?.flaggedComments ? (
            <Badge count={stats.flaggedComments} size="small" />
          ) : null}
        </Space>
      ),
      children: <ModerationTab />,
    },
  ];

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 12px' }}>
      {/* Page header */}
      <Title level={2} style={{ marginBottom: '16px' }}>
        Admin Panel
      </Title>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        style={{
          background: '#141414',
          padding: '8px 4px',
          borderRadius: '8px',
        }}
      />
    </div>
  );
}

export default AdminPage;
